import { Slot } from "@radix-ui/react-slot";
import { cn } from "@repo/design-system/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "border-1 border-white bg-primary text-white hover:border-primary hover:bg-white hover:text-primary",
        secondary:
          "border-1 border-brand bg-brand text-white hover:border hover:border-white hover:bg-secondary hover:text-white",
        ghost:
          "border-1 border-white bg-white text-primary hover:border-white hover:bg-transparent hover:text-white",
        outline:
          "border-1 border-white bg-transparent text-white hover:bg-white hover:text-primary",
        link: "border-none font-medium text-primary underline-offset-1 hover:underline",
        icon: "border-none bg-transparent text-primary shadow-none hover:text-primary-hover",
        accent:
          "border border-accent bg-accent py-1 text-accent-foreground hover:border hover:border-white hover:bg-secondary hover:text-white",
        purple: "bg-secondary text-white hover:bg-secondary/80",
        econ: "rounded-[14px] bg-white text-brand hover:bg-secondary hover:text-white",
      },
      size: {
        default: "h-12 px-4",
        sm: "h-9 rounded-lg px-3 text-xs",
        lg: "h-10 rounded-lg px-8",
        icon: "rounded-lg p-0",
      },
      backgroundColor: {
        default: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export interface ButtonPropsAsLink
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      data-slot="button"
      {...props}
    />
  );
}

Button.displayName = "Button";

export { Button, buttonVariants };
